import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthenticatedUser, TokenPair } from '@optiflow/types';

export type AuthStatus = 'restoring' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  /**
   * Held in memory only, never persisted.
   *
   * A short-lived access token in localStorage is the easiest thing for an XSS payload to steal.
   * On reload the refresh token mints a new one, which also exercises rotation on every visit.
   */
  accessToken: string | null;
  refreshToken: string | null;
  /** Chosen branch. Sent as a header and re-validated by the API on every request. */
  activeBranchId: string | null;

  setSession: (user: AuthenticatedUser, tokens: TokenPair) => void;
  setAccessToken: (accessToken: string) => void;
  setUser: (user: AuthenticatedUser) => void;
  setActiveBranch: (branchId: string | null) => void;
  setStatus: (status: AuthStatus) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      status: 'restoring',
      user: null,
      accessToken: null,
      refreshToken: null,
      activeBranchId: null,

      setSession: (user, tokens) => {
        const current = get().activeBranchId;
        // Keep the chosen branch across a refresh, but drop it if access was revoked meanwhile.
        const stillAllowed = current && user.branches.some((branch) => branch.id === current);

        set({
          status: 'authenticated',
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          activeBranchId: stillAllowed ? current : user.defaultBranchId,
        });
      },

      setAccessToken: (accessToken) => set({ accessToken }),
      setUser: (user) => set({ user }),
      setActiveBranch: (activeBranchId) => set({ activeBranchId }),
      setStatus: (status) => set({ status }),

      clear: () =>
        set({
          status: 'unauthenticated',
          user: null,
          accessToken: null,
          refreshToken: null,
          activeBranchId: null,
        }),
    }),
    {
      name: 'optiflow.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        activeBranchId: state.activeBranchId,
      }),
    },
  ),
);

/** Non-reactive accessors for use outside React, such as the axios interceptors. */
export const authStore = {
  get: () => useAuthStore.getState(),
  set: useAuthStore.setState,
};
